import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link
} from 'react-router-dom';

import './AdminDashboard.scss';

const DEFAULT_METRICS = {
  courses: 0,
  members: 0,
  upcomingEvents: 0,
  unreadInquiries: 0,
  pendingReports: 0
};

export default function AdminDashboard() {
  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL || '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token = localStorage.getItem(
    'tg_admin_token'
  );

  const [
    metrics,
    setMetrics
  ] = useState(DEFAULT_METRICS);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!token) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${apiBaseUrl}/admin/dashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to load dashboard.'
          );
        }

        setMetrics({
          ...DEFAULT_METRICS,
          ...(data.metrics || {})
        });
      } catch (err) {
        setError(
          err.message ||
            'Unable to load dashboard.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [
    apiBaseUrl,
    token
  ]);

  return (
    <div className="admin-dashboard">
      <section className="admin-dashboard__intro">
        <div>
          <p className="admin-dashboard__eyebrow">
            Operations
          </p>

          <h2>
            The Tour Golfers
          </h2>

          <p>
            Manage the platform, course network,
            events, members, partnerships, and
            community operations from one place.
          </p>
        </div>
      </section>

      {error ? (
        <div className="admin-dashboard__error">
          {error}
        </div>
      ) : null}

      <section className="admin-dashboard__metrics">
        <Link
          to="/admin/courses"
          className="admin-metric-card"
        >
          <div className="admin-metric-card__top">
            <span className="admin-metric-card__label">
              Courses
            </span>

            <span className="admin-metric-card__icon">
              ◉
            </span>
          </div>

          <strong>
            {loading
              ? '—'
              : metrics.courses}
          </strong>

          <span className="admin-metric-card__footer">
            Course database
          </span>
        </Link>

        <Link
          to="/admin/events"
          className="admin-metric-card"
        >
          <div className="admin-metric-card__top">
            <span className="admin-metric-card__label">
              Upcoming Events
            </span>

            <span className="admin-metric-card__icon">
              ◫
            </span>
          </div>

          <strong>
            {loading
              ? '—'
              : metrics.upcomingEvents}
          </strong>

          <span className="admin-metric-card__footer">
            Scheduled events
          </span>
        </Link>

        <Link
          to="/admin/members"
          className="admin-metric-card"
        >
          <div className="admin-metric-card__top">
            <span className="admin-metric-card__label">
              Members
            </span>

            <span className="admin-metric-card__icon">
              ◌
            </span>
          </div>

          <strong>
            {loading
              ? '—'
              : metrics.members}
          </strong>

          <span className="admin-metric-card__footer">
            Registered accounts
          </span>
        </Link>

        <Link
          to="/admin/inquiries"
          className={`admin-metric-card ${
            metrics.unreadInquiries > 0
              ? 'admin-metric-card--attention'
              : ''
          }`}
        >
          <div className="admin-metric-card__top">
            <span className="admin-metric-card__label">
              Unread Inquiries
            </span>

            <span className="admin-metric-card__icon">
              ✉
            </span>
          </div>

          <strong>
            {loading
              ? '—'
              : metrics.unreadInquiries}
          </strong>

          <span className="admin-metric-card__footer">
            Needs review
          </span>
        </Link>

        <Link
          to="/admin/reports"
          className={`admin-metric-card ${
            metrics.pendingReports > 0
              ? 'admin-metric-card--attention'
              : ''
          }`}
        >
          <div className="admin-metric-card__top">
            <span className="admin-metric-card__label">
              Pending Reports
            </span>

            <span className="admin-metric-card__icon">
              !
            </span>
          </div>

          <strong>
            {loading
              ? '—'
              : metrics.pendingReports}
          </strong>

          <span className="admin-metric-card__footer">
            Moderation queue
          </span>
        </Link>
      </section>

      <div className="admin-dashboard__grid">
        <section className="admin-dashboard-card">
          <div className="admin-dashboard-card__header">
            <div>
              <p className="admin-dashboard-card__eyebrow">
                Operations
              </p>

              <h3>
                Quick Actions
              </h3>
            </div>
          </div>

          <div className="admin-dashboard-actions">
            <Link
              to="/admin/courses/new"
              className="admin-dashboard-action admin-dashboard-action--primary"
            >
              <span className="admin-dashboard-action__plus">
                +
              </span>

              <div>
                <strong>
                  Add Course
                </strong>

                <span>
                  Create a new course profile
                </span>
              </div>
            </Link>

            <Link
              to="/admin/events/new"
              className="admin-dashboard-action"
            >
              <span className="admin-dashboard-action__plus">
                +
              </span>

              <div>
                <strong>
                  Create Event
                </strong>

                <span>
                  Schedule a Tour event
                </span>
              </div>
            </Link>

            <Link
              to="/admin/inquiries"
              className="admin-dashboard-action"
            >
              <span className="admin-dashboard-action__plus">
                →
              </span>

              <div>
                <strong>
                  Review Inquiries
                </strong>

                <span>
                  Member and partner requests
                </span>
              </div>
            </Link>

            <Link
              to="/admin/reports"
              className="admin-dashboard-action"
            >
              <span className="admin-dashboard-action__plus">
                →
              </span>

              <div>
                <strong>
                  Review Reports
                </strong>

                <span>
                  Platform moderation queue
                </span>
              </div>
            </Link>
          </div>
        </section>

        <section className="admin-dashboard-card">
          <div className="admin-dashboard-card__header">
            <div>
              <p className="admin-dashboard-card__eyebrow">
                Platform
              </p>

              <h3>
                Admin Priorities
              </h3>
            </div>
          </div>

          <div className="admin-priority-list">
            <Link
              to="/admin/inquiries"
              className="admin-priority-item"
            >
              <div>
                <strong>
                  Unread inquiries
                </strong>

                <span>
                  Review new member and
                  partnership messages
                </span>
              </div>

              <span className="admin-priority-item__count">
                {loading
                  ? '—'
                  : metrics.unreadInquiries}
              </span>
            </Link>

            <Link
              to="/admin/reports"
              className="admin-priority-item"
            >
              <div>
                <strong>
                  Moderation reports
                </strong>

                <span>
                  Review pending member reports
                </span>
              </div>

              <span className="admin-priority-item__count">
                {loading
                  ? '—'
                  : metrics.pendingReports}
              </span>
            </Link>

            <Link
              to="/admin/courses"
              className="admin-priority-item"
            >
              <div>
                <strong>
                  Course database
                </strong>

                <span>
                  Add, verify, and maintain
                  course profiles
                </span>
              </div>

              <span className="admin-priority-item__arrow">
                →
              </span>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}